// /app/analytics — workspace performance overview.
//
// Backend: GET /v1/analytics?days=N&limit=N (content-service v0.2.70+).
//
// Visual layout ported from design-handoff/src/pages/secondary.jsx
// AnalyticsPage — inline styles + tokens.css utility classes
// (.card, .eyebrow, .mono, .t-num). NO Tailwind.

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useBrands } from "@/hooks/use-persona";
import { usePostDrawer } from "@/components/PostDrawer";
import { PageHead, KPI, BrandMark, PlatformChip } from "@/components/primitives";
import { Icons } from "@/components/icons";
import type { Brand, Platform } from "@/lib/types";

// Wire shape from content-service handlers/analytics.go
interface AnalyticsResponse {
  days: number;
  from: string;
  to: string;
  by_platform: Array<{
    platform: Platform;
    published_count: number;
    avg_engagement: number;
    total_impressions: number;
  }>;
  top_posts: Array<{
    post_id: string;
    brand_id: string;
    platform: Platform;
    text_snippet: string;
    published_at: string;
    engagement_score: number;
    likes: number;
    comments: number;
    shares: number;
    platform_permalink?: string;
  }>;
  timeseries: Array<{ day: string; published_count: number }>;
}

export const Route = createFileRoute("/app/analytics")({
  component: AnalyticsPage,
});

const PLATFORM_LABEL: Record<Platform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  x: "X",
  linkedin: "LinkedIn",
  telegram: "Telegram",
};

// Series colours for the engagement legend — matches handoff palette.
const SERIES_COLOR: Record<Platform, string> = {
  instagram: "#3B82F6",
  facebook: "#22C55E",
  linkedin: "#F59E0B",
  x: "#A855F7",
  telegram: "#26A5E4",
};

function AnalyticsPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const [days, setDays] = useState<7 | 28 | 90>(28);

  const { data: brandsData } = useBrands();
  const brandMap = new Map<string, Brand>(
    (brandsData?.data ?? []).map((b) => [b.id, b]),
  );

  const { data, isLoading } = useQuery({
    queryKey: ["analytics", days],
    queryFn: () => api<AnalyticsResponse>(`/analytics?days=${days}&limit=10`),
    staleTime: 60_000,
  });

  // Brand health per-brand. Brand list response doesn't include
  // health_score (backend doesn't populate it), so we fetch from the
  // dedicated workspace brand-health endpoint that the home page +
  // BrandHealthGuide modal already use. Result map keyed by brand id.
  const { data: workspaceHealth } = useQuery({
    queryKey: ["workspace", "brand-health"],
    queryFn: () =>
      api<{ brands: Array<{ id: string; trade_name: string; score: number }> }>(
        "/workspace/brand-health",
      ),
    staleTime: 30_000,
  });
  const healthMap = new Map<string, number>(
    (workspaceHealth?.brands ?? []).map((b) => [b.id, b.score]),
  );

  const total = (data?.by_platform ?? []).reduce(
    (a, p) => a + p.published_count,
    0,
  );
  const totalImpressions = (data?.by_platform ?? []).reduce(
    (a, p) => a + p.total_impressions,
    0,
  );
  const avgEngAcrossAll = (() => {
    const valid = (data?.by_platform ?? []).filter((p) => p.published_count > 0);
    if (valid.length === 0) return 0;
    const sum = valid.reduce((a, p) => a + p.avg_engagement, 0);
    return sum / valid.length;
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? "09 · Analitik" : "09 · Analytics"}
        title={tr ? "Tüm markaların performansı." : "Performance across all brands."}
        subtitle={
          tr
            ? "Ne çalışıyor, ne çalışmıyor, kime yardım gerekiyor."
            : "What's working, what's not, who needs help."
        }
        actions={
          <>
            <WindowPicker days={days} onChange={setDays} tr={tr} />
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (!data) return;
                downloadAnalyticsCsv(data, days, tr);
              }}
              disabled={!data}
            >
              <Icons.Download size={13} /> {tr ? "Rapor indir" : "Export report"}
            </button>
          </>
        }
      />

      <div className="scroll-y" style={{ flex: 1, padding: "24px 32px 48px" }}>
        {/* KPI row — 2-col on phones via @media in tokens.css */}
        <div
          className="kpi-grid-4"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <KPI
            label={tr ? "Toplam erişim" : "Total reach"}
            value={fmtCompact(totalImpressions)}
            sub={
              total > 0 && totalImpressions === 0
                ? tr
                  ? "metrikler senkronize ediliyor (6 saatte bir)"
                  : "metrics catching up (sampled every 6h)"
                : tr
                ? `son ${days} gün`
                : `last ${days} days`
            }
          />
          <KPI
            label={tr ? "Ort. etkileşim" : "Avg engagement"}
            value={avgEngAcrossAll.toFixed(2)}
            sub={
              total > 0 && avgEngAcrossAll === 0
                ? tr
                  ? "metrikler senkronize ediliyor"
                  : "metrics catching up"
                : tr
                ? "tüm markalarda"
                : "across all brands"
            }
          />
          <KPI
            label={tr ? "Yayınlanan" : "Posts shipped"}
            value={total}
            sub={tr ? `son ${days} gün` : `last ${days} days`}
          />
          <KPI
            label={tr ? "Aktif platform" : "Active platforms"}
            value={(data?.by_platform ?? []).filter((p) => p.published_count > 0).length}
            sub={tr ? "yayın yapıldı" : "with posts"}
          />
        </div>

        {/* Timeseries card */}
        <div className="card" style={{ padding: 22, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div className="eyebrow" style={{ marginBottom: 4 }}>
                — {tr ? "Yayın temposu" : "Publish cadence"}
              </div>
              <h2 style={{ fontSize: 18 }}>
                {tr ? `Son ${days} gün · günlük yayın` : `Last ${days} days · daily publishes`}
              </h2>
            </div>
            <div style={{ display: "flex", gap: 14 }}>
              {(["instagram", "facebook", "linkedin", "x"] as Platform[]).map((pl) => (
                <div
                  key={pl}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    color: "var(--tx2)",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: SERIES_COLOR[pl],
                    }}
                  />
                  {PLATFORM_LABEL[pl]}
                </div>
              ))}
            </div>
          </div>
          {isLoading || !data ? (
            <div className="skel" style={{ height: 220, borderRadius: 6 }} />
          ) : (
            <BigChart timeseries={data.timeseries} />
          )}
        </div>

        {/* Top posts + brand health */}
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
          <div className="card" style={{ padding: 22 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              — {tr ? "En çok etkileşim alan" : "Top performing posts"}
            </div>
            <TopPosts
              data={data?.top_posts ?? []}
              brandMap={brandMap}
              isLoading={isLoading}
              tr={tr}
            />
          </div>
          <div className="card" style={{ padding: 22 }}>
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              — {tr ? "Marka sağlığı" : "Brand health"}
            </div>
            <BrandHealth brands={brandsData?.data ?? []} healthMap={healthMap} tr={tr} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Window picker — small pill group used in PageHead actions ──────────────

function WindowPicker({
  days,
  onChange,
  tr,
}: {
  days: 7 | 28 | 90;
  onChange: (d: 7 | 28 | 90) => void;
  tr: boolean;
}) {
  const opts: Array<7 | 28 | 90> = [7, 28, 90];
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      {opts.map((n) => {
        const on = n === days;
        return (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={on ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
            style={{ padding: "5px 10px" }}
          >
            {tr ? `${n} gün` : `${n}d`}
          </button>
        );
      })}
    </div>
  );
}

// ── Big chart — SVG line+area for daily timeseries ─────────────────────────
//
// The handoff version stacks 4 platform series (one polyline each). Our
// /analytics endpoint only returns a single { day, published_count }
// timeseries (no per-platform breakdown), so we render that as the
// primary line and tint with the accent. Once content-service ships
// per-platform timeseries we can stack them per the handoff's design.

function BigChart({ timeseries }: { timeseries: Array<{ day: string; published_count: number }> }) {
  const w = 800;
  const h = 220;
  const data = timeseries.length > 0 ? timeseries : [{ day: "", published_count: 0 }];
  const max = Math.max(1, ...data.map((d) => d.published_count));
  const pts = data
    .map(
      (d, i) =>
        `${(i / Math.max(1, data.length - 1)) * w},${h - (d.published_count / max) * (h - 10) - 5}`,
    )
    .join(" ");

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ width: "100%" }}>
      {[0, 0.25, 0.5, 0.75, 1].map((r) => (
        <line
          key={r}
          x1="0"
          x2={w}
          y1={r * h}
          y2={r * h}
          stroke="var(--b1)"
          strokeDasharray={r === 1 ? "0" : "2 4"}
        />
      ))}
      <polyline
        points={pts}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill="var(--accent)" opacity="0.08" />
    </svg>
  );
}

// ── Top posts list ─────────────────────────────────────────────────────────

function TopPosts({
  data,
  brandMap,
  isLoading,
  tr,
}: {
  data: AnalyticsResponse["top_posts"];
  brandMap: Map<string, Brand>;
  isLoading: boolean;
  tr: boolean;
}) {
  const open = usePostDrawer((s) => s.open);

  if (isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skel" style={{ height: 36 }} />
        ))}
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--mu)", padding: "12px 0" }}>
        {tr
          ? "Henüz etkileşim verisi yok. Yayınlanan gönderiler 6 saatte bir ölçülür."
          : "No engagement data yet. Published posts are sampled every 6h."}
      </div>
    );
  }
  return (
    <>
      {data.map((p, i) => {
        const b = brandMap.get(p.brand_id);
        return (
          <button
            key={p.post_id}
            onClick={() => open(p.post_id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 0",
              borderBottom: i < data.length - 1 ? "1px solid var(--b1)" : "none",
              width: "100%",
              textAlign: "left",
            }}
          >
            <span className="mono" style={{ fontSize: 11, color: "var(--mu)", width: 20 }}>
              {i + 1}
            </span>
            {b ? <BrandMark brand={b} size={22} /> : <div style={{ width: 22, height: 22 }} />}
            <PlatformChip platform={p.platform} size={18} />
            <span
              style={{
                flex: 1,
                fontSize: 12.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {p.text_snippet || (tr ? "(metin yok)" : "(no text)")}
            </span>
            <span
              className="mono"
              style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}
            >
              {p.engagement_score.toFixed(2)}
            </span>
          </button>
        );
      })}
    </>
  );
}

// ── Brand health — uses /v1/workspace/brand-health (per-brand score)
// since /v1/brands doesn't populate health_score. v0.1.59.

function BrandHealth({
  brands,
  healthMap,
  tr,
}: {
  brands: Brand[];
  healthMap: Map<string, number>;
  tr: boolean;
}) {
  if (brands.length === 0) {
    return (
      <div style={{ fontSize: 12.5, color: "var(--mu)", padding: "12px 0" }}>
        {tr ? "Marka yok." : "No brands yet."}
      </div>
    );
  }
  return (
    <>
      {brands.map((b) => {
        const score = healthMap.get(b.id) ?? 0;
        const health = score / 100;
        const color =
          health > 0.8 ? "var(--success)" : health > 0.7 ? "var(--warning)" : "var(--error)";
        const accent = b.accent_hex ?? "var(--accent)";
        return (
          <div key={b.id} style={{ padding: "8px 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <BrandMark brand={b} size={16} />
              <span style={{ fontSize: 12.5, flex: 1 }}>{b.trade_name}</span>
              <span className="mono" style={{ fontSize: 11, color }}>
                {Math.round(health * 100)}%
              </span>
            </div>
            <div
              style={{
                height: 3,
                background: "var(--elev)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{ height: "100%", width: `${health * 100}%`, background: accent }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// Builds an analytics CSV from the in-memory response and triggers a
// browser download. No server roundtrip — operator can take this to
// Excel / Google Sheets and present to clients. Two sheet-like
// sections joined by blank rows so it survives one-table CSV import.
function downloadAnalyticsCsv(data: AnalyticsResponse, days: number, tr: boolean) {
  const esc = (v: string | number) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows: string[] = [];

  // Section A — KPIs per platform
  rows.push(esc(tr ? `Analitik · son ${days} gün` : `Analytics · last ${days} days`));
  rows.push("");
  rows.push(
    [
      tr ? "Platform" : "Platform",
      tr ? "Yayın" : "Published",
      tr ? "Etkileşim Ort." : "Avg engagement",
      tr ? "Gösterim" : "Impressions",
    ].map(esc).join(","),
  );
  for (const p of data.by_platform) {
    rows.push(
      [
        p.platform,
        p.published_count,
        p.avg_engagement.toFixed(2),
        p.total_impressions,
      ].map(esc).join(","),
    );
  }

  // Section B — top posts
  rows.push("");
  rows.push(esc(tr ? "En iyi gönderiler" : "Top posts"));
  rows.push("");
  rows.push(
    [
      tr ? "Platform" : "Platform",
      tr ? "Gönderi" : "Snippet",
      tr ? "Yayınlandı" : "Published at",
      tr ? "Beğeni" : "Likes",
      tr ? "Yorum" : "Comments",
      tr ? "Paylaşım" : "Shares",
      tr ? "Etkileşim Skoru" : "Engagement",
      "URL",
    ].map(esc).join(","),
  );
  for (const t of data.top_posts) {
    rows.push(
      [
        t.platform,
        t.text_snippet,
        t.published_at,
        t.likes,
        t.comments,
        t.shares,
        t.engagement_score.toFixed(2),
        t.platform_permalink ?? "",
      ].map(esc).join(","),
    );
  }

  // Section C — timeseries
  rows.push("");
  rows.push(esc(tr ? "Günlük yayın" : "Daily published"));
  rows.push("");
  rows.push([tr ? "Tarih" : "Day", tr ? "Yayın sayısı" : "Published count"].map(esc).join(","));
  for (const t of data.timeseries) {
    rows.push([t.day, t.published_count].map(esc).join(","));
  }

  // BOM so Excel reads UTF-8 (Turkish chars) correctly. blob → click.
  const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `nexus-analytics-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
